import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PublicClientApplication, AccountInfo, InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import { apiService } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  userId: string;
  userEmail: string;
  login: () => Promise<void>;
  logout: () => void;
  setLocalUser: (userId: string, email: string) => void;
}

const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: import.meta.env.VITE_AZURE_AUTHORITY || '',
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage' as const,
    storeAuthStateInCookie: false,
  },
};

// Scope for the holmesglen-virtual-assistant API. Access tokens MUST be
// requested against this scope (not Microsoft Graph's user.read) so the
// online Function App's token_handler can verify them — tokens issued for
// Microsoft Graph carry a `nonce` header that prevents any other party
// from validating the signature, which surfaces as
// `jwt.exceptions.InvalidSignatureError: Signature verification failed`
// in the FA logs.
const apiScopes = [`api://${msalConfig.auth.clientId}/user_impersonation`];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [userId, setUserId] = useState<string>('local-dev-user');
  const [userEmail, setUserEmail] = useState<string>('dev@local.com');
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

  const enableAuth = import.meta.env.VITE_ENABLE_AUTH === 'true';

  console.log('[AUTH DEBUG] VITE_ENABLE_AUTH raw value:', import.meta.env.VITE_ENABLE_AUTH);
  console.log('[AUTH DEBUG] enableAuth computed:', enableAuth);
  console.log('[AUTH DEBUG] msalConfig.auth.clientId:', msalConfig.auth.clientId);

  useEffect(() => {
    const initAuth = async () => {
      console.log('[AUTH DEBUG] initAuth called, enableAuth:', enableAuth);
      if (enableAuth) {
        // Production mode - use Azure AD
        try {
          console.log('[AUTH DEBUG] Creating MSAL instance with config:', msalConfig);
          const instance = new PublicClientApplication(msalConfig);
          console.log('[AUTH DEBUG] Initializing MSAL...');
          await instance.initialize();
          console.log('[AUTH DEBUG] MSAL initialized successfully');
          setMsalInstance(instance);

          // Register a token provider so the API client can fetch a fresh
          // access token before every request. MSAL caches valid tokens and
          // only triggers a network refresh when the cached one is expired
          // (or about to be), so this is cheap on the hot path and prevents
          // 401s from stale tokens in a long browser session.
          //
          // The provider accepts an optional { force } flag. On the request
          // path we call it with no arg → silent-only. On a 401 response
          // the API client calls it with force=true → fall back to popup
          // or redirect if silent refresh can't succeed (e.g. because
          // third-party cookies are blocked or the underlying Entra
          // session has ended). This is what stops users from seeing the
          // "Authentication failed" toast on stale sessions.
          apiService.setTokenProvider(async (opts?: { force?: boolean }) => {
            const accounts = instance.getAllAccounts();
            if (accounts.length === 0) {
              if (opts?.force) {
                await instance.loginRedirect({
                  scopes: [...apiScopes, 'openid', 'profile', 'email'],
                });
              }
              return null;
            }
            const account = accounts[0];

            try {
              const response = await instance.acquireTokenSilent({
                scopes: apiScopes,
                account,
              });
              return response.accessToken;
            } catch (err) {
              // Silent path failed. If the caller asked for force, or the
              // failure is specifically because interaction is required,
              // try popup → then redirect.
              const interactionNeeded =
                err instanceof InteractionRequiredAuthError ||
                err instanceof BrowserAuthError;

              if (!opts?.force && !interactionNeeded) {
                console.error('Silent token acquisition failed:', err);
                return null;
              }

              // Try popup first — less disruptive than a full redirect.
              try {
                const response = await instance.acquireTokenPopup({
                  scopes: apiScopes,
                  account,
                });
                return response.accessToken;
              } catch (popupErr) {
                console.warn(
                  'Interactive token popup failed; falling back to redirect',
                  popupErr
                );
                // Last-resort: redirect. Page will reload post-login and
                // MSAL will hand back a fresh token on the return path.
                await instance.acquireTokenRedirect({
                  scopes: apiScopes,
                  account,
                });
                return null;
              }
            }
          });

          // Handle redirect promise first (for returning from login)
          console.log('[AUTH DEBUG] Handling redirect promise...');
          const redirectResponse = await instance.handleRedirectPromise();
          console.log('[AUTH DEBUG] Redirect response:', redirectResponse);

          if (redirectResponse) {
            console.log('[AUTH DEBUG] Redirect response received, setting account');
            setUser(redirectResponse.account);
            setUserId(redirectResponse.account.localAccountId);
            setUserEmail(redirectResponse.account.username);
            setIsAuthenticated(true);
            apiService.setAuthToken(redirectResponse.accessToken);
          } else {
            // Check if user is already logged in
            const accounts = instance.getAllAccounts();
            console.log('[AUTH DEBUG] Found accounts:', accounts.length);

            if (accounts.length > 0) {
              console.log('[AUTH DEBUG] User already logged in, setting account');
              const account = accounts[0];
              setUser(account);
              setUserId(account.localAccountId);
              setUserEmail(account.username);
              setIsAuthenticated(true);

              // Get an access token scoped to our own API (not Graph) and
              // hand it to the API client so token_handler can verify it.
              try {
                const response = await instance.acquireTokenSilent({
                  scopes: apiScopes,
                  account: account,
                });
                apiService.setAuthToken(response.accessToken);
              } catch (error) {
                console.error('Error acquiring token:', error);
              }
            } else {
              console.log('[AUTH DEBUG] No accounts found - user needs to login');
              console.log('[AUTH DEBUG] Triggering login redirect...');
              // No accounts found, redirect to login. Request the API scope
              // alongside openid/profile/email so the post-redirect response
              // already contains a valid API access token.
              await instance.loginRedirect({
                scopes: [...apiScopes, 'openid', 'profile', 'email'],
              });
            }
          }
        } catch (error) {
          console.error('MSAL initialization error:', error);
        }
      } else {
        // Local dev mode - bypass authentication
        console.log('[AUTH DEBUG] Auth disabled, using local dev user');
        setIsAuthenticated(true);
      }
    };

    initAuth();
  }, [enableAuth]);

  const login = async () => {
    if (!enableAuth) {
      console.log('Auth is disabled in local dev mode');
      return;
    }

    if (!msalInstance) {
      throw new Error('MSAL instance not initialized');
    }

    try {
      const response = await msalInstance.loginPopup({
        scopes: [...apiScopes, 'openid', 'profile', 'email'],
      });

      if (response.account) {
        setUser(response.account);
        setUserId(response.account.localAccountId);
        setUserEmail(response.account.username);
        setIsAuthenticated(true);
        apiService.setAuthToken(response.accessToken);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    if (enableAuth && msalInstance) {
      msalInstance.logoutPopup();
    }
    setUser(null);
    setUserId('local-dev-user');
    setUserEmail('dev@local.com');
    setIsAuthenticated(false);
    apiService.setAuthToken(null);
    apiService.setTokenProvider(null);
  };

  const setLocalUser = (newUserId: string, email: string) => {
    if (!enableAuth) {
      setUserId(newUserId);
      setUserEmail(email);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        userId,
        userEmail,
        login,
        logout,
        setLocalUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
